/**
 * signup-leads-api.ts — the Growth abandonment report's client (ADR-073 §2.6–§2.7, built by
 * Amendment 5, 5 October 2026).
 *
 *   GET /admin/signup-leads   (account service, per zone; Cedar `growth:read`)
 *
 * Card-first means a visitor who stops before paying leaves no account — only a lead, which
 * expires 90 days after their last activity. This is the recovery list: who started, where they
 * stopped, what they were about to buy. Leads live in the zone their country derives, so the read
 * fans out across zones in the browser (ADR-122 Pattern 1) and tags each row with its zone.
 */
import { adminApiInZone } from './api-client';
import { fanOutZones, failedZoneLabels, throwIfAllZonesFailed, type PortalZone } from './zones';

/** ADR-073 §2.6 — where the visitor stopped. */
export type LeadSegment = 'never-started' | 'card-abandoned' | 'card-declined';

export interface SignupLeadRow {
  leadId: string;
  businessName: string;
  email: string;
  ownerName: string;
  website: string;
  jurisdiction: string;
  segment: LeadSegment;
  status: string;
  selectedTier: string;
  commitment: string;
  promoCode: string;
  declineReason: string;
  /** Payment confirmed but provisioning failed after something was created — needs a person. */
  failedAt: string;
  createdAt: string;
  lastActivityAt: string;
  /** Added by the client after the fan-out — the zone the lead lives in. */
  zone?: string;
}

export interface SignupLeadStats {
  total: number;
  neverStarted: number;
  cardAbandoned: number;
  cardDeclined: number;
  needsAttention: number;
}

export interface SignupLeadsResult {
  leads: SignupLeadRow[];
  stats: SignupLeadStats;
  /** Zones that did not answer — part of the answer, never hidden (a shorter list looks correct). */
  failedZones: string[];
}

interface ZonePage { leads: SignupLeadRow[]; stats: SignupLeadStats }

export async function listAllSignupLeads(): Promise<SignupLeadsResult> {
  const results = await fanOutZones(async (zone: PortalZone) => {
    const page = await adminApiInZone<ZonePage>(zone, '/admin/signup-leads');
    return [{ zone, page }];
  });
  throwIfAllZonesFailed(results);
  const pages = results.flatMap((r) => r.items);
  const sum = (k: keyof SignupLeadStats) => pages.reduce((n, p) => n + (p.page.stats?.[k] ?? 0), 0);
  return {
    leads: pages
      .flatMap(({ zone, page }) => (page.leads ?? []).map((l) => ({ ...l, zone: zone.code })))
      .sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt)),
    stats: {
      total: sum('total'),
      neverStarted: sum('neverStarted'),
      cardAbandoned: sum('cardAbandoned'),
      cardDeclined: sum('cardDeclined'),
      needsAttention: sum('needsAttention'),
    },
    failedZones: failedZoneLabels(results),
  };
}
